/**
 * 発注側アプリ（LIFF）の入力パーツ。**スマホ前提**＝タップ44px・文字16px以上（05 §5.3）。
 * 16px 未満だと iOS がフォーカス時にページを拡大してしまう。
 */

import type React from "react";

export const fieldStyle = {
  width: "100%",
  minHeight: 44,
  padding: "var(--kz-space-2)",
  border: "1px solid var(--kz-color-border)",
  borderRadius: "var(--kz-radius-sm)",
  font: "inherit",
  // 05 §5.3：**16px 未満にしない**＝iOS が入力時にズームして画面が飛ぶ
  fontSize: "var(--kz-font-size-md)",
  background: "var(--kz-color-surface)",
} as const;

export function Input({
  label,
  value,
  onChange,
  hint,
  ...rest
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value">) {
  return (
    <label style={{ display: "block", marginBottom: "var(--kz-space-3)" }}>
      <span style={{ display: "block", fontSize: "var(--kz-font-size-sm)", fontWeight: 600, marginBottom: 4 }}>
        {label}
      </span>
      <input value={value} onChange={(e) => onChange(e.target.value)} style={fieldStyle} {...rest} />
      {hint && (
        <span style={{ display: "block", marginTop: 4, fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
          {hint}
        </span>
      )}
    </label>
  );
}

/**
 * 数量ステッパー（05 §5.3：現場は手袋・移動中＝ボタンは 44×44 で大きく）。
 * M1.9 で Catalog.tsx から移動（Catalog ⇄ Cart の循環 import を作らないため）。
 */
export function Stepper({
  value,
  step,
  unit,
  onChange,
  label,
  small = false,
}: {
  value: number;
  step: number;
  unit: string;
  onChange: (v: number) => void;
  label: string;
  /** カート行用の詰めた見た目（Design `.qty-step.sm`。ボタンは 44px を守る）。 */
  small?: boolean;
}) {
  // ★M1.9b＝Design の `.qty-step`（−｜値｜＋ が1つの枠に結合した箱）。見た目はクラスが持つ。
  // ★下限（＝1ロットぶん）に達したら「−」は**押しても何も起きない**。空振りするボタンを
  //   押せる見た目のまま置くと、押した人は「反応していない」と読む（M1.7）。
  //   **タップ領域 44px と aria-label はそのまま**（無効でも寸法は変えない）。
  const atMin = value <= step;
  return (
    <span className={small ? "qty-step sm" : "qty-step"}>
      <button
        type="button"
        className="kz-pressable"
        disabled={atMin}
        style={{ opacity: atMin ? 0.45 : 1, cursor: atMin ? "not-allowed" : "pointer" }}
        aria-label={`${label} を ${step} 減らす`}
        // **0 以下にしない**（0 個の注文行を作らせない）
        onClick={() => onChange(Math.max(step, value - step))}
      >
        −
      </button>
      <span className="qv kz-num" aria-live="polite">
        {value}
        <small> {unit}</small>
      </span>
      <button
        type="button"
        className="kz-pressable"
        aria-label={`${label} を ${step} 増やす`}
        onClick={() => onChange(value + step)}
      >
        ＋
      </button>
    </span>
  );
}
